import { motion } from "framer-motion";
import { iconMap } from "@/app/lib/iconMap";

interface ComparisonRow {
  id: number;
  feature: string;
  leftValue?: string | null;
  rightValue?: string | null;
  leftIcon?: string | null;
  rightIcon?: string | null;
  left_icon?: string | null;
  right_icon?: string | null;
}

interface ComparisonTableProps {
  data: {
    id: number;
    type: string;
    title: string;
    titleHighlight?: string;
    subtitle: string | null;
    leftTitle: string;
    rightTitle: string;
    rows: ComparisonRow[];
  };
}

const iconMapLookup = Object.keys(iconMap).reduce(
  (lookup, key) => {
    const value = iconMap[key as keyof typeof iconMap];
    lookup[key.toLowerCase()] = value;
    lookup[key.replace(/[-_\s]+/g, "").toLowerCase()] = value;
    return lookup;
  },
  {} as Record<string, typeof iconMap[keyof typeof iconMap]>
);

function resolveIcon(iconName?: string | null) {
  if (!iconName || typeof iconName !== "string") return null;

  const normalized = iconName.trim().replace(/[-_\s]+/g, "").toLowerCase();
  return iconMapLookup[normalized] || iconMapLookup[iconName.trim().toLowerCase()] || null;
}

function getIconName(row: ComparisonRow, side: "left" | "right") {
  if (side === "left") {
    return row.leftIcon ?? row.left_icon ?? row.left_icon ?? null;
  }

  return row.rightIcon ?? row.right_icon ?? row.rightIcon  ?? null;
}

export default function ProductComparison({ data }: ComparisonTableProps) {
  if (!data) return null;

  return (
    <section className="relative py-6 lg:py-8">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5 lg:mb-12"
        >
          <h2 className="section-title !mb-2">
            {data?.title}{" "}
            <span className="section-highlight-title">
              {data?.titleHighlight}
            </span>
          </h2>
          {data?.subtitle && (
            <p className="section-description max-w-[680px] mx-auto">
              {data.subtitle}
            </p>
          )}
        </motion.div>

        <div className="overflow-hidden border rounded-lg border-white/10">
          <table className="w-full border-collapse ">
            <thead className="bg-white/10">
              <tr>
                <th className="w-2/5 py-4 px-6 text-left text-white font-semibold border-b border-white/10">
                  Features
                </th>
                <th className="w-1/5 py-4 px-6 text-center text-white font-semibold border-b border-white/10">
                  {data.leftTitle}
                </th>
                <th className="w-1/5 py-4 px-6 text-center text-white font-semibold border-b border-white/10">
                  {data.rightTitle}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const leftIconName = getIconName(row, "left");
                const rightIconName = getIconName(row, "right");
                const LeftIcon = resolveIcon(leftIconName);
                const RightIcon = resolveIcon(rightIconName);

                return (
                  <motion.tr
                    key={row.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.4 }}
                    className="border-b last:border-transparent border-white/10 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-4 px-6 text-white/90">{row.feature}</td>
                    <td className="py-4 px-6 text-center">
                      {row.leftValue ? (
                        <span className="text-white/90">{row.leftValue}</span>
                      ) : LeftIcon ? (
                        <div className="flex justify-center">
                          <LeftIcon className="w-5 h-5 text-white/70" />
                        </div>
                      ) : leftIconName ? (
                        <span className="text-red-500 text-xs">Icon "{leftIconName}" not found</span>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {row.rightValue ? (
                        <span className="text-white/90">{row.rightValue}</span>
                      ) : RightIcon ? (
                        <div className="flex justify-center">
                          <RightIcon className="w-5 h-5 text-white/70" />
                        </div>
                      ) : rightIconName ? (
                        <span className="text-red-500 text-xs">Icon "{rightIconName}" not found</span>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}